// client/src/components/mischief/MarkerCircle.js
//
// Wobbly hand-drawn ellipse. Circles a child element by measuring
// its rendered size and overlaying a sized SVG behind/over it.
//
// The SVG path is a rough ellipse authored by hand — not a perfect
// <ellipse> element. The wobble is what makes it feel drawn, and a
// deliberate "overshoot" at the endpoint (where the pen came back
// around and went a bit past the start) sells the effect.
//
// Usage:
//   <MarkerCircle>7</MarkerCircle>
//   <MarkerCircle color="red" strokeWidth={3}>UPSET</MarkerCircle>
//   <MarkerCircle padding="loose">Featured card</MarkerCircle>

import { useMemo } from 'react';

const COLOR_CLASS = {
  marker: 'text-marker',
  red:    'text-red',
  accent: 'text-accent',
};

// Padding controls how far the circle extends beyond the child's
// bounding box. Horizontal padding is roughly double the vertical
// because that's how a person circles a word by hand — you sweep
// wider than you go tall. Percentages of the child's own dimensions,
// applied via negative inset on the SVG wrapper.
const PADDING_INSET = {
  tight:  { x: '-10%', y: '-6%'  },
  normal: { x: '-18%', y: '-10%' },
  loose:  { x: '-28%', y: '-16%' },
};

// Two hand-authored ellipse paths. Picking randomly between them
// per-instance means two adjacent MarkerCircles won't be identical
// — the same way you'd never draw the same circle twice.
//
// viewBox is 100x100; the paths draw an ellipse roughly filling it
// with an intentional overshoot at the closing point.
//
// The paths use cubic Béziers (C command) to curve — not perfect
// arcs. That's where the wobble lives.
const CIRCLE_PATHS = [
  // Variant A — leans slightly left, overshoots at top
  'M 50 8 C 78 8, 95 24, 94 50 C 93 76, 76 92, 50 92 C 24 92, 6 76, 6 50 C 6 24, 22 8, 50 8 C 60 8, 68 10, 74 12',
  // Variant B — leans slightly right, overshoots at bottom
  'M 52 10 C 24 10, 8 26, 8 52 C 8 78, 26 92, 52 92 C 78 92, 94 76, 92 50 C 90 24, 74 10, 52 10 C 46 10, 40 12, 36 14',
];

const pickPath = () =>
  CIRCLE_PATHS[Math.floor(Math.random() * CIRCLE_PATHS.length)];

export function MarkerCircle({
  children,
  color = 'marker',
  strokeWidth = 2.5,
  padding = 'normal',
  className = '',
}) {
  // Stable path across re-renders — otherwise the wobble would
  // re-roll on every render and jitter unpleasantly.
  const path = useMemo(pickPath, []);

  const inset = PADDING_INSET[padding] ?? PADDING_INSET.normal;
  const colorClass = COLOR_CLASS[color] ?? COLOR_CLASS.marker;

  return (
    // relative wrapper so the SVG can position against the child's
    // bounding box. inline-block so we don't force a new block line.
    <span className={`relative inline-block ${colorClass} ${className}`}>
      {/* The SVG. preserveAspectRatio="none" is key — it lets the
          100x100 path stretch to fit the child's actual aspect ratio,
          so a wide phrase gets an oval and a single digit gets a
          near-circle. That's the auto-sizing at work. */}
      <svg
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          top: inset.y, bottom: inset.y, left: inset.x, right: inset.x,
          width:  `calc(100% + 2 * -1 * ${inset.x})`,
          height: `calc(100% + 2 * -1 * ${inset.y})`,
        }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {/* The child sits above the circle. z-10 forces it above the
          absolute-positioned SVG sibling in the stacking order. */}
      <span className="relative z-10">{children}</span>
    </span>
  );
}